import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosRequestConfig } from "axios";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { DataTable } from "components/Table/DataTable";
import { Trash2 } from "lucide-react";
import { useDebounce } from "pages/CreateDeal/newdeal";
import { getCalculations, getLiveRates } from "pages/GenericOrder/apis";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import ReactSelect from "react-select";
import {
  getCurrency,
  GetCurrencyRequestType,
} from "service/apis/CreateForexQuotation";
import { toast } from "sonner";
import { formatCurrencyGeneric } from "utils";

export const forexProduct = [
  {
    label: "Forex Currency",
    value: "9",
  },
  {
    label: "Forex Card",
    value: "16",
  },
];

interface ForexProductItem {
  id: string;
  product: string;
  productLabel: string;
  currency: string;
  quantity: string;
  rate: string;
  service_charge: string;
  gst_included: boolean;
  // Calculated values
  amount: number;
  gst_on_fx: number;
  service_charges: number;
  service_charge_gst: number;
  total_payable: number;
  forex_Type: string
}

export default function ForexQuote({reset}:{reset: (fn: () => void) => void;}) {
  const { setValue, watch } = useFormContext();
  const [currencydata, setCurrencydata] = useState<
    { label: string; value: string }[]
  >([]);
  const [products, setProducts] = useState<ForexProductItem[]>([]);
  const [totals, setTotals] = useState({ 
    total_amount: 0, 
    total_gst_on_fx: 0,
    total_product_payable: 0,
    service_charges: 0,
    service_charge_gst: 0,
    grand_total: 0,
    gst_on_fx: 0 
  });

  // Form state for current product being entered
  const [currentProduct, setCurrentProduct] = useState({
    product: '',
    currency: 'USD',
    quantity: '',
    rate: '',
    service_charge: '',
    gst_included: false,
    forexType: ""
  });

  // Overall service charge state
  const [overallServiceCharge, setOverallServiceCharge] = useState('');
  const [isGstIncluded, setIsGstIncluded] = useState(false);
  const [liveRate, setLiveRate] = useState('');

  const fetchLiveRate = async () => {
    if (!currentProduct.currency || !watch("fx")) return;
    
    try {
      const rate = await getLiveRates({
        currency: currentProduct.currency.toUpperCase(),
        type: watch("fx") === "sale" ? "Buy" : "Sell",
        fx_product: currentProduct.product === "16" ? "Card" : "Currency",
      });      
      setLiveRate(rate?.rates?.toString() || '');
      // Set the live rate as default if rate field is empty
      if(watch('fx')=== 'encashment'){
        if(currentProduct.product === "16"){
          updateCurrentProduct('rate', '');
        }
        else{
          updateCurrentProduct('rate', rate?.rates?.toString() || '');
        }
      }
      else{
        updateCurrentProduct('rate', rate?.rates?.toString() || '');
      }
    } catch (error) {
      console.error('Error fetching live rate:', error);
      toast.error('Failed to fetch live rate');
    }
  };

  const fx = watch("fx");

  useEffect(() => {
    if (currentProduct?.currency && currentProduct?.product && fx) {
      fetchLiveRate();
    }
  }, [currentProduct?.currency, currentProduct?.product, fx]);

  useEffect(() => {
    setValue("fx", "encashment");
    fetchCurrency();
  }, []);
  const fetchCurrency = () => {
    const payload: AxiosRequestConfig<GetCurrencyRequestType> = {};
    getCurrency(payload)
      .then((res) => {
        setCurrencydata(
          res.data.map((item) => {
            return {
              label: item.currency,
              value: item.currency,
            };
          }),
        );
      })
      .catch(() => { });
  };

  // Calculate function for individual product (without service charge)
  const calculateProduct = async (productData: typeof currentProduct): Promise<ForexProductItem> => {
    const quantity = parseFloat(productData.quantity) || 0;
    const rate = parseFloat(productData.rate) || 0;
    const fx_type = watch("fx");

    let total_payable = 0;
    let gst_on_fx = 0;
    let amount = 0;

    if (quantity && rate) {
      amount = quantity * rate;
      // const gst_new = await getCalculations({
      //   amount: amount,
      //   type: fx_type === "sale" ? "sell" : "buy",
      // });
      // gst_on_fx = gst_new?.gst?.gst || 0;

      // Simple calculation without service charge - only amount and GST on FX
      // if (fx_type === "sale") {
      //   total_payable = amount + gst_on_fx;
      // } else {
      //   total_payable = amount - gst_on_fx;
      // }
      total_payable = amount;
    }

    const productLabel = forexProduct.find(p => p.value === productData.product)?.label || '';

    return {
      id: crypto.randomUUID(),
      product: productData.product,
      productLabel,
      currency: productData.currency,
      quantity: productData.quantity,
      rate: productData.rate,
      service_charge: '', // Not used at product level
      gst_included: false, // Not used at product level
      amount,
      gst_on_fx,
      service_charges: 0, // Will be calculated at total level
      service_charge_gst: 0, // Will be calculated at total level
      total_payable,
      forex_Type: watch("fx")
    };
  };

  // Calculate totals for all products with overall service charge
  const calculateTotals = async () => {
    const fx_type = watch("fx");
    const service_charge = parseFloat(overallServiceCharge) || 0;
    
    // Calculate product totals
    const productTotals = products.reduce(
      (acc, product) => ({
        total_amount: acc.total_amount + product.amount,
        total_gst_on_fx: acc.total_gst_on_fx + product.gst_on_fx,
        total_product_payable: acc.total_product_payable + product.total_payable,
      }),
      { total_amount: 0, total_gst_on_fx: 0, total_product_payable: 0 }
    );

    let service_charges = 0;
    let service_charge_gst = 0;
    let grand_total = productTotals.total_product_payable;
    let gst_on_fx = 0;
    const gst_new = await getCalculations({
      amount: productTotals?.total_amount,
      type: fx_type === "sale" ? "sell" : "buy",
    });

    gst_on_fx = gst_new?.gst?.gst || 0;

    // Calculate service charges if provided
    if (service_charge > -1) {
      if (isGstIncluded) {
        // GST is included in the service charge
        service_charges = service_charge;
        service_charge_gst = (service_charge / 1.18) * 0.18;
      } else {
        // GST needs to be added to service charge
        service_charge_gst = (service_charge * 18) / 100;
        service_charges = service_charge + service_charge_gst;
      }

      // Apply service charge based on FX type
      if (fx_type === "sale") {
        grand_total += (service_charges + gst_on_fx);
      } else {
        grand_total -= (service_charges + gst_on_fx);
      }
    }

    // Set form values for totals
    setValue("cal_gst", gst_on_fx.toFixed(2));
    setValue("cal_service_charge", service_charges.toString());
    setValue("cal_total_payable", grand_total.toString());
    setValue("forex_products", products);
    setValue("overall_service_charge", overallServiceCharge);
    setValue("gst_included", isGstIncluded);

    return {
      ...productTotals,
      service_charges,
      service_charge_gst,
      grand_total,
      gst_on_fx,
    };
  };

  // Add new product
  const addProduct = async () => {
    // Validate required fields
    if (!currentProduct.product || !currentProduct.quantity || !currentProduct.rate) {
      toast.warning("Please fill in all required fields (Product, Quantity, Rate)");
      return;
    }

    const calculatedProduct = await calculateProduct(currentProduct);
    setProducts([...products, calculatedProduct]);
    // Reset form for next product
    setCurrentProduct({
      product: '',
      currency: 'USD',
      quantity: '',
      rate: '',
      service_charge: '',
      gst_included: false,
      forexType: ""
    });
  };

  // Remove product
  const removeProduct = (id: string) => {
    setProducts(products.filter(product => product.id !== id));
  };

  // Update current product field
  const updateCurrentProduct = (field: keyof typeof currentProduct, value: any) => {
    setCurrentProduct(prev => ({ ...prev, [field]: value }));
  };

  // Update overall service charge
  const updateOverallServiceCharge = (value: string) => {
    setOverallServiceCharge(value);
  };

  // Update GST included flag
  const updateGstIncluded = (value: boolean) => {
    setIsGstIncluded(value);
  };

  useDebounce(() => {
    calculateTotals().then(calculatedTotals => {
      setTotals(calculatedTotals);
    });
  },1000, [products, overallServiceCharge, isGstIncluded, watch("fx")]);

  useEffect(() => {
    setValue("fx", "encashment");
    fetchCurrency();
  }, []);

  const defineColumns: ColumnDef<any>[] = [
    {
      accessorKey: "product",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Product" />
      ),
      cell: ({ row }) => <div className="w-[80px]">{row.original.productLabel}</div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "forex_amount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Forex amount" />
      ),
      cell: ({ row }) => <div className="w-[80px] flex gap-2 items-start flex-col">
        <div className="">
          <span className="">{row.original.currency} </span>
          <span>{row.original.quantity}</span>
        </div>
        <span className="bg-[#FFFED9] py-1 px-2.5 rounded-md whitespace-nowrap">live: <span className="text-blue-700">{formatCurrencyGeneric(row.original.rate)}</span></span>
        <span className="text-xs text-gray-500">(Default: {formatCurrencyGeneric(row.original.rate)})</span>
      </div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "inr_amount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="INR amount" />
      ),
      cell: ({ row }) => <div className="w-[80px]">{formatCurrencyGeneric(row.original.amount)}</div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'action',
      cell: ({ row }) => <div className="w-0">
        <button
          type="button"
          onClick={() => removeProduct(row.original.id)}
          className="text-red-600 hover:text-red-800 p-1"
          title="Remove product"
        >
          <Trash2 color="red" />
        </button>
      </div>,
      enableSorting: false,
      enableHiding: false,
    },
  ]

  const resetStates = () => {
    setProducts([])
    setOverallServiceCharge('')
    setCurrentProduct({
      product: '',
      currency: 'USD',
      quantity: '',
      rate: '',
      service_charge: '',
      gst_included: false,
      forexType: ""
    })
    setIsGstIncluded(false)
    setTotals({
      total_amount: 0,
      total_gst_on_fx: 0,
      total_product_payable: 0,
      service_charges: 0,
      service_charge_gst: 0,
      grand_total: 0,
      gst_on_fx: 0
    })    
    setValue("cal_gst", '');
    setValue("cal_service_charge", '');
    setValue("cal_total_payable", '');
    setValue("forex_products", []);
    setValue("overall_service_charge", '');
    setValue("gst_included", false);
  }

   useEffect(() => {
    reset?.(resetStates);
  }, []);

  return (
    <section className="w-full h-full flex flex-col gap-4">
      {/* FX Type Selection */}
      <div className="grid grid-cols-2 sm:grid-cols-1 gap-4">
        <div className="space-y-2 col-span-2">
          <label
            htmlFor="fxCharges"
            className="text-sm font-normal leading-[17px] text-[#302929]"
          >
            FX
          </label>
          <RadioGroup
            className="flex font-normal text-sm lg:text-base"
            onValueChange={(value: "encashment" | "sale") => {                          
              setValue("fx", value);
              setProducts([])                          
              setOverallServiceCharge('')
              setCurrentProduct({
                product: '',
                currency: 'USD',
                quantity: '',
                rate: '',
                service_charge: '',
                gst_included: false,
                forexType: value
              })
              setIsGstIncluded(false)
              setTotals({
                total_amount: 0,
                total_gst_on_fx: 0,
                total_product_payable: 0,
                service_charges: 0,
                service_charge_gst: 0,
                grand_total: 0,
                gst_on_fx: 0
              })
            }}
            value={watch("fx")}
            aria-label="fx"
            name="fx"
            id="fx"
            defaultValue="encashment"
          >
            <label
              htmlFor="encashment"
              className={` ${watch("fx") === "encashment" ? " border-blue-700" : " "} flex items-center rounded-lg border p-3 h-12 space-x-2 w-full`}
            >
              <RadioGroupItem
                value="encashment"
                id="encashment"
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
              />
              <p>Encashment</p>
            </label>
            <label
              htmlFor="sale"
              className={` ${watch("fx") === "sale" ? "border-blue-700" : ""} flex items-center rounded-lg border p-3 h-12 space-x-2 w-full`}
            >
              <RadioGroupItem
                value="sale"
                id="sale"
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
              />
              <p>Sale</p>
            </label>
          </RadioGroup>
        </div>
      </div>

      {/* Single Product Entry Form */}
      <div className="border rounded-lg p-2 lg:p-4 bg-gray-50">
        <div className="grid grid-cols-2 sm:grid-cols-1 gap-4">
          {/* Product Selection */}
          <div className="space-y-2 sm:col-span-2">
            <label className="text-sm font-normal leading-[17px] text-[#666666]">
              Select product *
            </label>
            <ReactSelect
              className="w-full font-normal text-sm lg:text-base"
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  zIndex: 0,
                  borderRadius: "0.5rem",
                  height: "3rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderColor: state.isFocused ? "#165FE3" : "#d2d2d2",
                  boxShadow: "none",
                }),
              }}
              isMulti={false}
              name="current_product"
              isSearchable={false}
              options={forexProduct}
              value={
                currentProduct.product
                  ? forexProduct.find((item) => item.value === currentProduct.product)
                  : null
              }
              onChange={(option) => {
                updateCurrentProduct('product', option?.value || '');
              }}
              placeholder="Select product"
            />
          </div>

          {/* Currency & Quantity */}
          <div className="relative space-y-2 sm:col-span-2">
            <label className="text-sm font-normal leading-[17px] text-[#666666]">
              Currency & Quantity *
            </label>
            <div className="relative w-full rounded-md">
              <div className="relative inset-y-0 top-1 left-0 flex items-center pl-2.5">
                <ReactSelect
                  className="absolute font-normal text-sm lg:text-base"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      zIndex: 0,
                      position: "absolute",
                      borderRadius: "0.5rem",
                      height: "2.5rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "none",
                      boxShadow: "none",
                      backgroundColor: "#E1EBFC",
                      width: "90px",
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: "#165FE3",
                      fontSize: "14px",
                      fontWeight: "500",
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: "#165FE3",
                      fontSize: "14px",
                      fontWeight: "400"
                    }),
                    indicatorSeparator: (provided) => ({
                      ...provided,
                      display: "none",
                    }),
                  }}
                  isMulti={false}
                  name="current_currency"
                  isSearchable
                  options={currencydata}
                  value={
                    currentProduct.currency
                      ? currencydata.find((item) => item.value === currentProduct.currency)
                      : null
                  }
                onChange={(option) => {
                    updateCurrentProduct('rate', '');
                    updateCurrentProduct('currency', option?.value || 'USD');
                  }}
                />
              </div>
              <Input
                type="number"
                onWheel={e=> e.currentTarget.blur()} // Prevents scroll zoom
                value={currentProduct.quantity}
                onChange={(e) => {
                  updateCurrentProduct('quantity', e.target.value);
                }}
                placeholder="Enter quantity"
                className="m-0 h-12 max-w-[340px] pl-[120px] border border-gray-border-gray-lite py-1.5 pr-[7px] rounded-lg text-sm lg:text-base"
              />
            </div>
          </div>

          {/* Rate */}
          <div className="relative space-y-2 sm:col-span-2">
            <label className="text-sm font-normal leading-[17px] text-[#666666]">
              Rate *
            </label>
            <div className="relative w-full rounded-md">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                <span className=" font-medium leading-4 -tracking-[1%] p-2.5 rounded-md bg-[#E1EBFC] text-blue-700 text-sm lg:text-base">
                  INR
                </span>
              </div>
              <Input
                type="number"
                onWheel={e=> e.currentTarget.blur()} 
                placeholder="Enter rate"
                value={currentProduct.rate}
                onChange={(e) => {
                  updateCurrentProduct('rate', e.target.value);
                }}
                className="m-0 h-12 max-w-[340px] pl-[70px] border border-gray-border-gray-lite py-1.5 pr-[7px] rounded-lg"
              />
            </div>
          </div>

          {/* Add Product Button */}
          <div className="col-span-2 flex justify-end">
            <button
              type="button"
              onClick={addProduct}
              className="flex items-center gap-2 text-[#165FE3] text-[15px] font-semibold"
            >
              <svg
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 15.7109C4.14 15.7109 1 12.5709 1 8.71094C1 4.85094 4.14 1.71094 8 1.71094C11.86 1.71094 15 4.85094 15 8.71094C15 12.5709 11.86 15.7109 8 15.7109ZM8 2.71094C4.69 2.71094 2 5.40094 2 8.71094C2 12.0209 4.69 14.7109 8 14.7109C11.31 14.7109 14 12.0209 14 8.71094C14 5.40094 11.31 2.71094 8 2.71094Z"
                  fill="#165FE3"
                />
                <path
                  d="M8 12.2109C7.72 12.2109 7.5 11.9909 7.5 11.7109V5.71094C7.5 5.43094 7.72 5.21094 8 5.21094C8.28 5.21094 8.5 5.43094 8.5 5.71094V11.7109C8.5 11.9909 8.28 12.2109 8 12.2109Z"
                  fill="#165FE3"
                />
                <path
                  d="M11 9.21094H5C4.72 9.21094 4.5 8.99094 4.5 8.71094C4.5 8.43094 4.72 8.21094 5 8.21094H11C11.28 8.21094 11.5 8.43094 11.5 8.71094C11.5 8.99094 11.28 9.21094 11 9.21094Z"
                  fill="#165FE3"
                />
              </svg>
              Add Product
            </button>
          </div>
        </div>
      </div>

      {/* Service Charge - Only show if products exist */}

        <>
          <div className="relative space-y-2 sm:col-span-2">
            <label className="text-sm font-normal leading-[17px] text-[#666666]">
              Product service charge
            </label>
            <div className="relative w-full rounded-md">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                <span className=" font-medium leading-4 -tracking-[1%] p-2.5 rounded-md bg-[#E1EBFC] text-blue-700 sm:text-sm">
                  INR
                </span>
              </div>
              <Input
                type="number"
                onWheel={e=> e.currentTarget.blur()} // Prevents scroll zoom
                placeholder="service charge"
                value={overallServiceCharge}
                onChange={(e) => {
                  updateOverallServiceCharge(e.target.value);
                }}
                className="m-0 h-12 max-w-[340px] pl-[70px] border border-gray-border-gray-lite py-1.5 pr-[7px] rounded-lg"
              />
            </div>
          </div>

          {/* GST Included Checkbox */}
          <div className="relative space-y-2 col-span-2">
            <label className="text-sm font-medium leading-[17px] flex items-center text-[#666666]">
              <input
                type="checkbox"
                checked={isGstIncluded}
                onChange={(e) => {
                  updateGstIncluded(e.target.checked);
                }}
                className="h-4 w-4 rounded border-gray-border-gray-lite border"
              />
              <span className="ml-2 text-sm font-medium leading-[17px] select-none text-[#666666]">
                This service charge includes GST
              </span>
            </label>
          </div>
        </>

      {/* Products Table */}
      {products.length > 0 && (
        <DataTable
          columns={defineColumns}
          data={products}
          isToolbarVisible={false}
          isPaginationVisible={false}
          isPageCountVisible={false}
        />
      )}

      {/* Overall Summary */}
      {products.length > 0 && (
        <section className="flex-1 bg-white-A700 py-5 rounded-xl gap-4 flex flex-col">
          <div className="pt-5 pb-10 px-5 border-b-8 rounded-xl bg-custom-calc-gradient border-blue-700 gap-10">
            <div className=" gap-5 flex flex-col">
              <div className="flex flex-col gap-4 w-full">
                <div className="flex justify-between w-full">
                  <span className="text-xs leading-[14.5px] indent-3.5 text-[#666666]">
                    Total Amount
                  </span>
                  <span className="text-sm font-semibold float-right leading-4 indent-3.5 text-[#333333]">
                    {`${formatCurrencyGeneric(totals.total_amount)}`}
                  </span>
                </div>
                <div className="flex justify-between w-full">
                  <span className="text-xs leading-[14.5px] indent-3.5 text-[#666666]">
                    Total GST on FX
                  </span>
                  <span className="text-sm font-semibold float-right leading-4 indent-3.5 text-[#333333]">
                    {`${formatCurrencyGeneric(totals.gst_on_fx)}`}
                  </span>
                </div>
                {overallServiceCharge && parseFloat(overallServiceCharge) > 0 && (
                  <div className="flex justify-between w-full">
                    <span className="text-xs leading-[14.5px] indent-3.5 text-[#666666]">
                      Service Charges {isGstIncluded ? '(Inc GST)' : '(+ GST)'}
                    </span>
                    <span className="text-sm font-semibold float-right leading-4 indent-3.5 text-[#333333]">
                      {`${formatCurrencyGeneric(totals.service_charges)}`}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex justify-between pt-2.5 text-blue-700 w-full">
                <span className="text-xs leading-[14.5px] indent-3.5 font-semibold">
                  Grand Total
                </span>
                <span className="text-sm font-semibold float-right leading-4 indent-3.5 text-[#333333]">
                  {`${formatCurrencyGeneric(Math.round(totals.grand_total))}`}
                </span>
              </div>
            </div>
          </div>
        </section>
      )}
    </section>
  );
}   